/**
 * Matching Engine Admin Picks-Derived schema — the participant snapshot's
 * `output`: the single legitimate fold (base `pick` + `pickRewrite` +
 * `pickWhyConnect` + `pickWhyConnectParagraph` overlays, in fold order) plus a
 * provenance map.
 *
 * `data` is the flattened `PickSet` (the pick-set the fold converges to), so the
 * Output view reads `output.data.picks` / `output.data.substitutes` directly —
 * not the `BackendPickData` envelope, whose extra `.data` nesting made the
 * composite cards render empty.
 *
 * `provenance` lists, per card, the ordered step-result ids that produced it
 * (base pick first, overlays in fold order).
 */

import { z } from "zod";
import { PersonIdSchema } from "./branded-id.schemas.js";
import { PickSetSchema } from "./pick-data.schemas.js";

/**
 * One card's provenance. A list rather than an object keyed by person: a JSON
 * object's keys are strings, so keying by person would strip the `PersonId` off
 * the wire, and this section exists to be cross-referenced against the pool and
 * the step results, which carry it.
 */
export const provenanceEntrySchema = z.object({
  personId: PersonIdSchema,
  stepResultIds: z.array(z.string()).readonly(),
});

export const provenanceSchema = z.array(provenanceEntrySchema).readonly();

export const picksDerivedSchema = z.object({
  data: PickSetSchema,
  provenance: provenanceSchema,
});

export type PicksDerived = z.infer<typeof picksDerivedSchema>;
